/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-18
 */

import { createApi } from '@reduxjs/toolkit/query/react';

// Add local dependencies.
import { baseQueryWithReauth } from '../../api/baseQuery';
import {
  Studio,
  StudioContext,
  StudioListParams,
  PaginatedResponse,
  CreateStudioRequest,
  UpdateStudioRequest,
  StudioRolesResponse,
} from '../../types/studio';
import {
  StudioMetrics,
  ActivityItem,
  ActivityParams,
} from '../../types/studioOverview';
import {
  StudioMember,
  StudioMembersParams,
  RemoveMemberRequest,
} from '../../types/studioMembers';
import {
  Invitation,
  InvitationListParams,
  SendInvitationRequest,
  InvitationMetrics,
} from '../../types/invitation';
import {
  JoinRequest,
  JoinRequestListParams,
  SubmitJoinRequestRequest,
  ApproveJoinRequestRequest,
  DenyJoinRequestRequest,
  JoinRequestMetrics,
  StudioSearchResult,
  StudioSearchParams,
} from '../../types/joinRequest';
import {
  RoleChange,
  RoleChangeRequest,
  BulkRoleChangeRequest,
  BulkRoleChangeResult,
  RoleManagementStats,
  GetRoleHistoryParams,
  GetRoleStatsParams,
} from '../../types/roleManagement';
import {
  MemberActivity,
  ActivityFilterParams,
  ActivityMetrics,
  EngagementData,
  MemberActivitySummary,
} from '../../types/memberActivity';
import {
  QuotaOverview,
  UsageHistory,
  UsageWarning,
  GetUsageMetricsParams,
  GetUsageMetricsResponse,
  GetQuotaOverviewParams,
  GetUsageHistoryParams,
  DismissWarningParams,
  UsageChartData,
  UsageChartOptions,
} from '../../types/usageTracking';
import {
  Invoice,
  InvoiceListParams,
  PaymentFailure,
  PaymentRetryRequest,
  PaymentRetryResponse,
  BillingMetricsResponse,
  BillingSettings,
  InvoiceDownloadResponse,
  BulkInvoiceDownloadResponse,
  InvoiceActionRequest,
  BulkInvoiceActionRequest,
} from '../../types/billingWorkflow';
import {
  ToolAccess,
  ToolAccessMatrix,
  GetToolAccessParams,
  UpdateToolAccessRequest,
  CheckToolAvailabilityRequest,
  AccessCheckResult,
  ToolAccessReport,
  BulkAccessUpdate,
  BulkAccessUpdateResult,
} from '../../types/toolAccess';
import {
  BillingDashboard,
  GetBillingDashboardRequest,
  GetInvoicesRequest,
  GetInvoicesResponse,
  DownloadInvoiceRequest,
  BulkInvoiceDownloadRequest,
} from '../../types/billingDashboard';

export const studiosApi = createApi({
  reducerPath: 'studiosApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Studio',
    'StudioContext',
    'StudioMembership',
    'Invitation',
    'JoinRequest',
    'RoleChange',
    'MemberActivity',
    'UsageMetrics',
    'UsageWarning',
    'Invoice',
    'PaymentFailure',
    'BillingSettings',
    'BillingDashboard',
    'ToolAccess',
  ],
  endpoints: builder => ({
    // Get user's studios with filtering and pagination
    getStudios: builder.query<PaginatedResponse<Studio>, StudioListParams>({
      query: params => ({
        url: 'studios/',
        params: {
          search: params.search,
          role: params.role,
          subscription_status: params.subscriptionStatus,
          ordering: params.ordering,
          page: params.page,
          page_size: params.pageSize || 12,
        },
      }),
      providesTags: ['Studio'],
    }),

    // Get a specific studio by ID
    getStudio: builder.query<Studio, string>({
      query: studioId => `studios/${studioId}/`,
      providesTags: (result, error, studioId) => [
        { type: 'Studio', id: studioId },
      ],
    }),

    // Create a new studio
    createStudio: builder.mutation<Studio, CreateStudioRequest>({
      query: studioData => {
        // If logo is provided, use FormData
        if (studioData.logo) {
          const formData = new FormData();
          formData.append('name', studioData.name);
          formData.append('description', studioData.description);
          formData.append('email', studioData.email);

          // Add business info fields as individual fields to match Django model
          if (studioData.businessInfo) {
            if (studioData.businessInfo.phone) {
              formData.append('phone', studioData.businessInfo.phone);
            }
            if (studioData.businessInfo.website) {
              formData.append('website', studioData.businessInfo.website);
            }
            // Note: address is handled separately in Django model as OneToOne relationship
          }

          // Add isPersonal if provided
          if (studioData.isPersonal !== undefined) {
            formData.append('is_personal', studioData.isPersonal.toString());
          }

          // Add join request settings if provided
          if (studioData.allowJoinRequests !== undefined) {
            formData.append(
              'allow_join_requests',
              studioData.allowJoinRequests.toString()
            );
          }

          if (studioData.joinRequestMessage !== undefined) {
            formData.append(
              'join_request_message',
              studioData.joinRequestMessage
            );
          }

          // Add logo file
          formData.append('logo', studioData.logo);

          return {
            url: 'studios/',
            method: 'POST',
            body: formData,
          };
        }

        // If no logo, use regular JSON body - map to Django model structure
        const requestBody = {
          name: studioData.name,
          description: studioData.description,
          email: studioData.email,
          // Map business info fields to individual Django model fields
          ...(studioData.businessInfo?.phone && {
            phone: studioData.businessInfo.phone,
          }),
          ...(studioData.businessInfo?.website && {
            website: studioData.businessInfo.website,
          }),
          ...(studioData.isPersonal !== undefined && {
            is_personal: studioData.isPersonal,
          }),
          // Add join request settings if provided
          ...(studioData.allowJoinRequests !== undefined && {
            allow_join_requests: studioData.allowJoinRequests,
          }),
          ...(studioData.joinRequestMessage !== undefined && {
            join_request_message: studioData.joinRequestMessage,
          }),
        };

        return {
          url: 'studios/',
          method: 'POST',
          body: requestBody,
        };
      },
      invalidatesTags: ['Studio'],
    }),

    // Update an existing studio
    updateStudio: builder.mutation<
      Studio,
      { id: string; data: UpdateStudioRequest }
    >({
      query: ({ id, data }) => {
        // If logo is provided, use FormData (similar to createStudio)
        if (data.logo) {
          const formData = new FormData();

          // Add basic fields
          if (data.name !== undefined) formData.append('name', data.name);
          if (data.description !== undefined)
            formData.append('description', data.description);

          // Add business info fields as individual fields to match Django model
          if (data.businessInfo) {
            if (data.businessInfo.phone) {
              formData.append('phone', data.businessInfo.phone);
            }
            if (data.businessInfo.website) {
              formData.append('website', data.businessInfo.website);
            }
            if (data.businessInfo.address) {
              formData.append('address', data.businessInfo.address);
            }
          }

          // Add join request settings if provided
          if (data.allowJoinRequests !== undefined) {
            formData.append(
              'allow_join_requests',
              data.allowJoinRequests.toString()
            );
          }
          if (data.joinRequestMessage !== undefined) {
            formData.append('join_request_message', data.joinRequestMessage);
          }

          // Add timezone and studio type if provided
          if (data.timezone !== undefined) {
            formData.append('timezone', data.timezone);
          }
          if (data.studioType !== undefined) {
            formData.append('studio_type', data.studioType);
          }

          // Add logo file
          formData.append('logo', data.logo);

          return {
            url: `studios/${id}/`,
            method: 'PATCH',
            body: formData,
          };
        }

        // If no logo, use regular JSON body - map to Django model structure
        const requestBody: any = {};

        if (data.name !== undefined) requestBody.name = data.name;
        if (data.description !== undefined)
          requestBody.description = data.description;

        // Map business info fields to individual Django model fields (same as FormData path)
        if (data.businessInfo) {
          if (data.businessInfo.phone !== undefined) {
            requestBody.phone = data.businessInfo.phone;
          }
          if (data.businessInfo.website !== undefined) {
            requestBody.website = data.businessInfo.website;
          }
          if (data.businessInfo.address !== undefined) {
            requestBody.address = data.businessInfo.address;
          }
        }

        if (data.settings !== undefined) requestBody.settings = data.settings;

        // Map allowJoinRequests to allow_join_requests for backend
        if (data.allowJoinRequests !== undefined) {
          requestBody.allow_join_requests = data.allowJoinRequests;
        }

        // Map joinRequestMessage to join_request_message for backend
        if (data.joinRequestMessage !== undefined) {
          requestBody.join_request_message = data.joinRequestMessage;
        }

        // Add timezone and studio type if provided
        if (data.timezone !== undefined) {
          requestBody.timezone = data.timezone;
        }
        if (data.studioType !== undefined) {
          requestBody.studio_type = data.studioType;
        }

        return {
          url: `studios/${id}/`,
          method: 'PATCH',
          body: requestBody,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Studio', id },
        'Studio',
      ],
    }),

    // Delete a studio
    deleteStudio: builder.mutation<void, string>({
      query: studioId => ({
        url: `studios/${studioId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Studio'],
    }),

    // Studio context management
    getCurrentContext: builder.query<StudioContext, void>({
      query: () => 'studios/current/',
      providesTags: ['StudioContext'],
    }),

    // Switch to a different studio
    switchStudio: builder.mutation<Studio, string>({
      query: studioId => ({
        url: `studios/${studioId}/switch/`,
        method: 'POST',
      }),
      invalidatesTags: ['StudioContext'],
    }),

    // Get recent studios for quick access
    getRecentStudios: builder.query<Studio[], void>({
      query: () => 'studios/recent/',
      providesTags: ['Studio'],
    }),

    // Get user's studios list (provides total count)
    getUserStudios: builder.query<
      {
        studios: Studio[];
        total: number;
      },
      void
    >({
      query: () => 'user/studios/',
      providesTags: ['Studio'],
    }),

    // Get available studio roles
    getStudioRoles: builder.query<StudioRolesResponse, void>({
      query: () => 'studios/roles/',
      // No tags needed as this is static data that rarely changes
    }),

    // Upload studio logo
    uploadStudioLogo: builder.mutation<
      { logoUrl: string },
      { studioId: string; file: File }
    >({
      query: ({ studioId, file }) => {
        const formData = new FormData();
        formData.append('logo', file);
        return {
          url: `studios/${studioId}/upload-logo/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'Studio', id: studioId },
        'Studio',
      ],
    }),

    // Studio Overview Endpoints
    // Get studio metrics for overview dashboard
    getStudioMetrics: builder.query<StudioMetrics, string>({
      query: studioId => `studios/${studioId}/metrics/`,
      providesTags: (result, error, studioId) => [
        { type: 'Studio', id: `${studioId}-metrics` },
      ],
    }),

    // Get recent activity for studio
    getStudioActivity: builder.query<
      PaginatedResponse<ActivityItem>,
      ActivityParams
    >({
      query: ({ studioId, limit = 10, activityType, page = 1 }) => ({
        url: `studios/${studioId}/activity/`,
        params: {
          limit,
          page,
          ...(activityType && { activity_type: activityType }),
        },
      }),
      providesTags: (result, error, { studioId }) => [
        { type: 'Studio', id: `${studioId}-activity` },
      ],
    }),

    // Studio Members Management Endpoints
    // Get studio members with filtering and pagination
    getStudioMembers: builder.query<
      PaginatedResponse<StudioMember>,
      StudioMembersParams
    >({
      query: ({ studioId, ...params }) => ({
        url: 'studios/memberships/',
        params: {
          studio: studioId,
          search: params.search,
          role: params.role,
          status: params.status,
          page: params.page,
          page_size: params.pageSize || 20,
        },
      }),
      providesTags: (result, error, { studioId }) => [
        { type: 'StudioMembership', id: studioId },
      ],
    }),

    // Change member role
    changeMemberRole: builder.mutation<StudioMember, RoleChangeRequest>({
      query: ({ membershipId, toRole, reason }) => ({
        url: `studio-memberships/${membershipId}/`,
        method: 'PATCH',
        body: { role: toRole, reason },
      }),
      invalidatesTags: ['StudioMembership', 'RoleChange'],
    }),

    // Remove member from studio
    removeMember: builder.mutation<void, RemoveMemberRequest>({
      query: ({ membershipId, reason }) => ({
        url: `studio-memberships/${membershipId}/`,
        method: 'DELETE',
        body: reason ? { reason } : undefined,
      }),
      invalidatesTags: ['StudioMembership'],
    }),

    // Get member details
    getMemberDetails: builder.query<StudioMember, string>({
      query: membershipId => `studio-memberships/${membershipId}/`,
      providesTags: (result, error, membershipId) => [
        { type: 'StudioMembership', id: membershipId },
      ],
    }),

    // Get studio member detail with full profile data
    getStudioMemberDetail: builder.query<
      StudioMember,
      { studioId: string; memberId: string }
    >({
      query: ({ studioId, memberId }) =>
        `studios/${studioId}/members/${memberId}/`,
      providesTags: (result, error, { studioId, memberId }) => [
        { type: 'StudioMembership', id: `${studioId}-${memberId}` },
      ],
    }),

    // Invitation Management Endpoints
    // Send invitation to new member
    sendInvitation: builder.mutation<Invitation, SendInvitationRequest>({
      query: ({ studioId, email, role, message }) => ({
        url: `studios/${studioId}/invitations/`,
        method: 'POST',
        body: {
          email,
          role,
          message,
        },
      }),
      invalidatesTags: ['Invitation', 'StudioMembership'],
    }),

    // Get studio invitations with filtering and pagination
    getInvitations: builder.query<
      PaginatedResponse<Invitation>,
      InvitationListParams
    >({
      query: ({ studioId, ...params }) => ({
        url: `studios/${studioId}/invitations/`,
        params: {
          search: params.search,
          status: params.status,
          role: params.role,
          page: params.page,
          page_size: params.pageSize || 20,
        },
      }),
      transformResponse: (response: any) => {
        // Transform soft-deleted invitations to show cancelled status
        if (response.results) {
          response.results = response.results.map((invitation: any) => {
            // If invitation has deleted_at field, it's been cancelled
            if (invitation.deleted_at) {
              return {
                ...invitation,
                status: 'cancelled',
                cancelledAt: invitation.deleted_at,
              };
            }
            return invitation;
          });
        }
        return response;
      },
      providesTags: (result, error, { studioId }) => [
        { type: 'Invitation', id: studioId },
      ],
    }),

    // Resend invitation
    resendInvitation: builder.mutation<
      Invitation,
      { studioId: string; invitationId: string }
    >({
      query: ({ studioId, invitationId }) => ({
        url: `studios/${studioId}/invitations/${invitationId}/resend/`,
        method: 'POST',
      }),
      invalidatesTags: ['Invitation'],
    }),

    // Cancel invitation
    cancelInvitation: builder.mutation<
      void,
      { studioId: string; invitationId: string }
    >({
      query: ({ studioId, invitationId }) => ({
        url: `studios/${studioId}/invitations/${invitationId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Invitation'],
    }),

    // Get invitation details
    getInvitationDetails: builder.query<Invitation, string>({
      query: invitationId => `studios/invitations/${invitationId}/`,
      providesTags: (result, error, invitationId) => [
        { type: 'Invitation', id: invitationId },
      ],
    }),

    // Get invitation metrics for studio
    getInvitationMetrics: builder.query<InvitationMetrics, string>({
      query: studioId => `studios/${studioId}/invitations/stats/`,
      providesTags: (result, error, studioId) => [
        { type: 'Invitation', id: `${studioId}-metrics` },
      ],
    }),

    // Join Request Management Endpoints
    // Submit join request to studio (using public endpoint)
    submitJoinRequest: builder.mutation<JoinRequest, SubmitJoinRequestRequest>({
      query: ({ studioId, studioSlug, message, requestedRole }) => {
        // Try to use studio slug for public endpoint, fallback to studio ID if needed
        const endpointParam = studioSlug || studioId;

        // The backend takes the requester's name and email from their account
        return {
          url: `public/studios/${endpointParam}/join/`,
          method: 'POST',
          body: {
            message,
            requested_role: requestedRole,
          },
        };
      },
      invalidatesTags: ['JoinRequest', 'StudioMembership'],
    }),

    // Get join requests for studio (admin view)
    getJoinRequests: builder.query<
      PaginatedResponse<JoinRequest>,
      JoinRequestListParams
    >({
      query: ({ studioId, ...params }) => ({
        url: `studios/${studioId}/join-requests/`,
        params: {
          ...(params.userId && { user: params.userId }),
          search: params.search,
          status: params.status,
          requested_role: params.requestedRole,
          page: params.page,
          page_size: params.pageSize || 20,
          ...(params.dateFrom && { date_from: params.dateFrom }),
          ...(params.dateTo && { date_to: params.dateTo }),
        },
      }),
      providesTags: (result, error, { studioId, userId }) => [
        { type: 'JoinRequest', id: studioId || userId || 'LIST' },
      ],
    }),

    // Get user's join requests
    getMyJoinRequests: builder.query<
      PaginatedResponse<JoinRequest>,
      JoinRequestListParams
    >({
      query: params => ({
        url: 'join-requests/my-requests/',
        params: {
          search: params.search,
          status: params.status,
          page: params.page,
          page_size: params.pageSize || 20,
        },
      }),
      providesTags: ['JoinRequest'],
    }),

    // Approve join request
    approveJoinRequest: builder.mutation<
      JoinRequest,
      ApproveJoinRequestRequest
    >({
      query: ({ requestId, assignedRole }) => ({
        url: `studios/join-requests/${requestId}/approve/`,
        method: 'POST',
        body: {
          role: assignedRole,
        },
      }),
      invalidatesTags: ['JoinRequest', 'StudioMembership'],
    }),

    // Deny join request
    denyJoinRequest: builder.mutation<JoinRequest, DenyJoinRequestRequest>({
      query: ({ requestId, reason, allowResubmission }) => ({
        url: `studios/join-requests/${requestId}/reject/`,
        method: 'POST',
        body: {
          reason,
          allow_resubmission: allowResubmission,
        },
      }),
      invalidatesTags: ['JoinRequest'],
    }),

    // Cancel join request (user action)
    cancelJoinRequest: builder.mutation<void, string>({
      query: requestId => ({
        url: `studios/join-requests/${requestId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['JoinRequest'],
    }),

    // Get join request details
    getJoinRequestDetails: builder.query<JoinRequest, string>({
      query: requestId => `studios/join-requests/${requestId}/`,
      providesTags: (result, error, requestId) => [
        { type: 'JoinRequest', id: requestId },
      ],
    }),

    // Get join request metrics for studio
    getJoinRequestMetrics: builder.query<JoinRequestMetrics, string>({
      query: studioId => `studios/join-requests/metrics/?studio=${studioId}`,
      providesTags: (result, error, studioId) => [
        { type: 'JoinRequest', id: `${studioId}-metrics` },
      ],
    }),

    // Search public studios for join requests
    searchPublicStudios: builder.query<
      PaginatedResponse<StudioSearchResult>,
      StudioSearchParams
    >({
      query: params => {
        const queryParams: any = {
          page: params.page || 1,
          page_size: params.pageSize || 20,
        };

        // Add search term if provided
        if (params.search) {
          queryParams.search = params.search;
        }

        // Add boolean parameters only if they are explicitly set
        if (params.isPublic !== undefined) {
          queryParams.is_public = params.isPublic;
        }
        if (params.allowJoinRequests !== undefined) {
          queryParams.allow_join_requests = params.allowJoinRequests;
        }
        if (params.minMembers !== undefined) {
          queryParams.min_members = params.minMembers;
        }
        if (params.maxMembers !== undefined) {
          queryParams.max_members = params.maxMembers;
        }

        return {
          url: 'studios/',
          params: queryParams,
        };
      },
      providesTags: ['Studio'],
    }),

    // Get browsable studios that allow join requests (using dedicated endpoint)
    getBrowsableStudios: builder.query<
      PaginatedResponse<StudioSearchResult>,
      StudioSearchParams
    >({
      query: params => ({
        url: 'studios/browsable/',
        params: {
          search: params.search,
          page: params.page || 1,
          page_size: params.pageSize || 20,
        },
      }),
      providesTags: ['Studio'],
    }),

    // Role Management Endpoints
    // Get role change history for studio or specific member
    getRoleHistory: builder.query<
      PaginatedResponse<RoleChange>,
      GetRoleHistoryParams
    >({
      query: ({ studioId, membershipId, limit = 20, offset = 0 }) => ({
        url: 'studios/role-changes/',
        params: {
          studio: studioId,
          ...(membershipId && { membership: membershipId }),
          limit,
          offset,
        },
      }),
      providesTags: (result, error, { studioId, membershipId }) => [
        { type: 'RoleChange', id: membershipId || studioId },
      ],
    }),

    // Bulk role change for multiple members
    bulkRoleChange: builder.mutation<
      BulkRoleChangeResult,
      BulkRoleChangeRequest
    >({
      query: ({ membershipIds, toRole, reason }) => ({
        url: 'studios/memberships/bulk-role-change/',
        method: 'POST',
        body: {
          membership_ids: membershipIds,
          role: toRole,
          reason,
        },
      }),
      invalidatesTags: ['StudioMembership', 'RoleChange'],
    }),

    // Get role management statistics for studio
    getRoleManagementStats: builder.query<
      RoleManagementStats,
      GetRoleStatsParams
    >({
      query: ({ studioId }) => `studios/${studioId}/role-stats/`,
      providesTags: (result, error, { studioId }) => [
        { type: 'RoleChange', id: `${studioId}-stats` },
      ],
    }),

    // Member Activity Dashboard Endpoints
    // Get member activities with filtering and pagination
    getMemberActivities: builder.query<
      PaginatedResponse<MemberActivity>,
      ActivityFilterParams
    >({
      query: ({ studioId, ...params }) => ({
        url: 'studios/member-activities/',
        params: {
          studio: studioId,
          ...(params.memberId && { member: params.memberId }),
          ...(params.activityTypes && {
            activity_types: params.activityTypes.join(','),
          }),
          ...(params.dateFrom && { date_from: params.dateFrom }),
          ...(params.dateTo && { date_to: params.dateTo }),
          ...(params.search && { search: params.search }),
          ...(params.success !== undefined && { success: params.success }),
          page: params.page || 1,
          page_size: params.pageSize || 20,
          ordering: params.ordering || '-timestamp',
        },
      }),
      providesTags: (result, error, { studioId, memberId }) => [
        { type: 'MemberActivity', id: memberId || studioId },
      ],
    }),

    // Get activity metrics for studio
    getActivityMetrics: builder.query<ActivityMetrics, ActivityFilterParams>({
      query: ({ studioId, ...params }) => ({
        url: `studios/${studioId}/activity-metrics/`,
        params: {
          ...(params.memberId && { member: params.memberId }),
          ...(params.dateFrom && { date_from: params.dateFrom }),
          ...(params.dateTo && { date_to: params.dateTo }),
          ...(params.activityTypes && {
            activity_types: params.activityTypes.join(','),
          }),
        },
      }),
      providesTags: (result, error, { studioId, memberId }) => [
        { type: 'MemberActivity', id: `${memberId || studioId}-metrics` },
      ],
    }),

    // Get engagement data for charts
    getEngagementData: builder.query<
      EngagementData[],
      {
        studioId: string;
        timeRange?: '7d' | '30d' | '90d' | '1y';
        memberId?: string;
      }
    >({
      query: ({ studioId, timeRange = '30d', memberId }) => ({
        url: `studios/${studioId}/engagement-data/`,
        params: {
          time_range: timeRange,
          ...(memberId && { member: memberId }),
        },
      }),
      providesTags: (result, error, { studioId, memberId }) => [
        { type: 'MemberActivity', id: `${memberId || studioId}-engagement` },
      ],
    }),

    // Get member activity summary
    getMemberActivitySummary: builder.query<
      MemberActivitySummary,
      { studioId: string; memberId: string }
    >({
      query: ({ studioId, memberId }) =>
        `studios/${studioId}/members/${memberId}/activity-summary/`,
      providesTags: (result, error, { studioId: _studioId, memberId }) => [
        { type: 'MemberActivity', id: `${memberId}-summary` },
      ],
    }),

    // Usage Tracking Endpoints
    // Get usage metrics for studio
    getUsageMetrics: builder.query<
      GetUsageMetricsResponse,
      GetUsageMetricsParams
    >({
      query: ({ studioId, includeHistory = false, historyDays = 30 }) => ({
        url: `studios/${studioId}/usage-metrics/`,
        params: {
          include_history: includeHistory,
          history_days: historyDays,
        },
      }),
      providesTags: (result, error, { studioId }) => [
        { type: 'UsageMetrics', id: studioId },
      ],
    }),

    // Get quota overview for studio
    getQuotaOverview: builder.query<QuotaOverview, GetQuotaOverviewParams>({
      query: ({ studioId }) => `studios/${studioId}/quota-overview/`,
      providesTags: (result, error, { studioId }) => [
        { type: 'UsageMetrics', id: `${studioId}-quota` },
      ],
    }),

    // Get usage history for charts
    getUsageHistory: builder.query<UsageHistory[], GetUsageHistoryParams>({
      query: ({ studioId, startDate, endDate, granularity = 'daily' }) => ({
        url: `studios/${studioId}/usage-history/`,
        params: {
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
          granularity,
        },
      }),
      providesTags: (result, error, { studioId }) => [
        { type: 'UsageMetrics', id: `${studioId}-history` },
      ],
    }),

    // Get usage chart data
    getUsageChartData: builder.query<
      UsageChartData,
      { studioId: string; options: UsageChartOptions }
    >({
      query: ({ studioId, options }) => ({
        url: `studios/${studioId}/usage-chart-data/`,
        params: {
          time_range: options.timeRange,
          categories: options.categories.join(','),
          show_limits: options.showLimits,
          show_trends: options.showTrends,
        },
      }),
      providesTags: (result, error, { studioId }) => [
        { type: 'UsageMetrics', id: `${studioId}-chart` },
      ],
    }),

    // Dismiss usage warning
    dismissUsageWarning: builder.mutation<void, DismissWarningParams>({
      query: ({ warningId, dismissUntil }) => ({
        url: `studios/usage-warnings/${warningId}/dismiss/`,
        method: 'POST',
        body: dismissUntil ? { dismiss_until: dismissUntil.toISOString() } : {},
      }),
      invalidatesTags: ['UsageWarning', 'UsageMetrics'],
    }),

    // Get active usage warnings for studio
    getUsageWarnings: builder.query<UsageWarning[], string>({
      query: studioId => `studios/${studioId}/usage-warnings/`,
      providesTags: (result, error, studioId) => [
        { type: 'UsageWarning', id: studioId },
      ],
    }),

    // Billing Workflow Endpoints
    // Get invoices for studio with filtering and pagination
    getInvoices: builder.query<PaginatedResponse<Invoice>, InvoiceListParams>({
      query: ({ studioId, ...params }) => ({
        url: 'billing/invoices/',
        params: {
          studio: studioId,
          search: params.search,
          status: params.status,
          date_from: params.dateFrom,
          date_to: params.dateTo,
          min_amount: params.minAmount,
          max_amount: params.maxAmount,
          page: params.page,
          page_size: params.pageSize || 20,
          ordering: params.ordering || '-issue_date',
        },
      }),
      providesTags: (result, error, { studioId }) => [
        { type: 'Invoice', id: studioId },
      ],
    }),

    // Get invoice details
    getInvoiceDetails: builder.query<Invoice, string>({
      query: invoiceId => `billing/invoices/${invoiceId}/`,
      providesTags: (result, error, invoiceId) => [
        { type: 'Invoice', id: invoiceId },
      ],
    }),

    // Download invoice PDF
    downloadInvoice: builder.mutation<InvoiceDownloadResponse, string>({
      query: invoiceId => ({
        url: `billing/invoices/${invoiceId}/download/`,
        method: 'POST',
      }),
    }),

    // Bulk download invoices
    bulkDownloadInvoices: builder.mutation<
      BulkInvoiceDownloadResponse,
      string[]
    >({
      query: invoiceIds => ({
        url: 'billing/invoices/bulk-download/',
        method: 'POST',
        body: { invoice_ids: invoiceIds },
      }),
    }),

    // Perform action on invoice
    performInvoiceAction: builder.mutation<void, InvoiceActionRequest>({
      query: ({ invoiceId, action, metadata }) => ({
        url: `billing/invoices/${invoiceId}/actions/`,
        method: 'POST',
        body: { action, metadata },
      }),
      invalidatesTags: ['Invoice'],
    }),

    // Perform bulk action on invoices
    performBulkInvoiceAction: builder.mutation<void, BulkInvoiceActionRequest>({
      query: ({ invoiceIds, action, metadata }) => ({
        url: 'billing/invoices/bulk-actions/',
        method: 'POST',
        body: { invoice_ids: invoiceIds, action, metadata },
      }),
      invalidatesTags: ['Invoice'],
    }),

    // Get payment failures for studio
    getPaymentFailures: builder.query<PaymentFailure[], string>({
      query: studioId => `billing/payment-failures/?studio=${studioId}`,
      providesTags: (result, error, studioId) => [
        { type: 'PaymentFailure', id: studioId },
      ],
    }),

    // Retry failed payment
    retryPayment: builder.mutation<PaymentRetryResponse, PaymentRetryRequest>({
      query: ({ invoiceId, paymentMethodId, updatePaymentMethod }) => ({
        url: `billing/invoices/${invoiceId}/retry-payment/`,
        method: 'POST',
        body: {
          payment_method_id: paymentMethodId,
          update_payment_method: updatePaymentMethod,
        },
      }),
      invalidatesTags: ['Invoice', 'PaymentFailure'],
    }),

    // Dismiss payment failure notification
    dismissPaymentFailure: builder.mutation<void, string>({
      query: failureId => ({
        url: `billing/payment-failures/${failureId}/dismiss/`,
        method: 'POST',
      }),
      invalidatesTags: ['PaymentFailure'],
    }),

    // Get billing metrics for studio
    getBillingMetrics: builder.query<
      BillingMetricsResponse,
      { studioId: string; timeRange?: '7d' | '30d' | '90d' | '1y' }
    >({
      query: ({ studioId, timeRange = '30d' }) => ({
        url: `billing/studios/${studioId}/metrics/`,
        params: { time_range: timeRange },
      }),
      providesTags: (result, error, { studioId }) => [
        { type: 'Invoice', id: `${studioId}-metrics` },
      ],
    }),

    // Get billing settings for studio
    getBillingSettings: builder.query<BillingSettings, string>({
      query: studioId => `billing/studios/${studioId}/settings/`,
      providesTags: (result, error, studioId) => [
        { type: 'BillingSettings', id: studioId },
      ],
    }),

    // Update billing settings
    updateBillingSettings: builder.mutation<
      BillingSettings,
      { studioId: string; settings: Partial<BillingSettings> }
    >({
      query: ({ studioId, settings }) => ({
        url: `billing/studios/${studioId}/settings/`,
        method: 'PATCH',
        body: settings,
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'BillingSettings', id: studioId },
      ],
    }),

    // Tool Access Control Endpoints
    // Get tool access matrix for studio
    getToolAccess: builder.query<ToolAccessMatrix, GetToolAccessParams>({
      query: ({ studioId, toolId, category }) => ({
        url: `studios/${studioId}/tool-access/`,
        params: {
          ...(toolId && { tool_id: toolId }),
          ...(category && { category }),
        },
      }),
      providesTags: (result, error, { studioId }) => [
        { type: 'ToolAccess', id: studioId },
      ],
    }),

    // Update tool access permissions
    updateToolAccess: builder.mutation<
      ToolAccess,
      { studioId: string; toolId: string; updates: UpdateToolAccessRequest }
    >({
      query: ({ studioId, toolId, updates }) => ({
        url: `studios/${studioId}/tool-access/${toolId}/`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'ToolAccess', id: studioId },
      ],
    }),

    // Check tool availability for user
    checkToolAvailability: builder.query<
      AccessCheckResult,
      CheckToolAvailabilityRequest
    >({
      query: ({ studioId, toolId, userId }) => ({
        url: `studios/${studioId}/tool-access/${toolId}/check/`,
        params: {
          ...(userId && { user_id: userId }),
        },
      }),
      providesTags: (result, error, { studioId, toolId }) => [
        { type: 'ToolAccess', id: `${studioId}-${toolId}` },
      ],
    }),

    // Generate tool access report
    generateToolAccessReport: builder.query<ToolAccessReport, string>({
      query: studioId => `studios/${studioId}/tool-access/report/`,
      providesTags: (result, error, studioId) => [
        { type: 'ToolAccess', id: `${studioId}-report` },
      ],
    }),

    // Bulk update tool access
    bulkUpdateToolAccess: builder.mutation<
      BulkAccessUpdateResult,
      { studioId: string; updates: BulkAccessUpdate }
    >({
      query: ({ studioId, updates }) => ({
        url: `studios/${studioId}/tool-access/bulk-update/`,
        method: 'POST',
        body: updates,
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'ToolAccess', id: studioId },
      ],
    }),

    // Reset tool access to defaults
    resetToolAccessDefaults: builder.mutation<ToolAccessMatrix, string>({
      query: studioId => ({
        url: `studios/${studioId}/tool-access/reset-defaults/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, studioId) => [
        { type: 'ToolAccess', id: studioId },
      ],
    }),

    // Billing Dashboard Endpoints
    // Get comprehensive billing dashboard data
    getBillingDashboard: builder.query<
      BillingDashboard,
      GetBillingDashboardRequest
    >({
      query: ({
        studioId,
        includeActivity = true,
        activityLimit = 5,
        includeInvoices = true,
        invoiceLimit = 3,
      }) => ({
        url: `billing/studios/${studioId}/dashboard/`,
        params: {
          include_activity: includeActivity,
          activity_limit: activityLimit,
          include_invoices: includeInvoices,
          invoice_limit: invoiceLimit,
        },
      }),
      providesTags: (result, error, { studioId }) => [
        { type: 'BillingDashboard', id: studioId },
      ],
    }),

    // Get invoices with enhanced filtering for dashboard
    getBillingInvoices: builder.query<GetInvoicesResponse, GetInvoicesRequest>({
      query: ({
        studioId,
        page = 1,
        pageSize = 20,
        status,
        search,
        ordering = '-created_date',
        dateFrom,
        dateTo,
      }) => ({
        url: 'billing/invoices/',
        params: {
          studio: studioId,
          page,
          page_size: pageSize,
          ...(status && { status: status.join(',') }),
          ...(search && { search }),
          ...(ordering && { ordering }),
          ...(dateFrom && { date_from: dateFrom }),
          ...(dateTo && { date_to: dateTo }),
        },
      }),
      providesTags: (result, error, { studioId }) => [
        { type: 'Invoice', id: `${studioId}-dashboard` },
      ],
    }),

    // Download single invoice
    downloadBillingInvoice: builder.mutation<Blob, DownloadInvoiceRequest>({
      query: ({ invoiceId, format = 'pdf' }) => ({
        url: `billing/invoices/${invoiceId}/download/`,
        method: 'POST',
        body: { format },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    // Bulk download invoices
    bulkDownloadBillingInvoices: builder.mutation<
      Blob,
      BulkInvoiceDownloadRequest
    >({
      query: ({ invoiceIds, format = 'zip' }) => ({
        url: 'billing/invoices/bulk-download/',
        method: 'POST',
        body: { invoice_ids: invoiceIds, format },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
  }),
});

export const {
  useGetStudiosQuery,
  useGetStudioQuery,
  useCreateStudioMutation,
  useUpdateStudioMutation,
  useDeleteStudioMutation,
  useUploadStudioLogoMutation,
  useGetCurrentContextQuery,
  useLazyGetCurrentContextQuery,
  useSwitchStudioMutation,
  useGetRecentStudiosQuery,
  useGetUserStudiosQuery,
  useGetStudioRolesQuery,
  useGetStudioMetricsQuery,
  useGetStudioActivityQuery,
  useGetStudioMembersQuery,
  useChangeMemberRoleMutation,
  useRemoveMemberMutation,
  useGetMemberDetailsQuery,
  useGetStudioMemberDetailQuery,
  useSendInvitationMutation,
  useGetInvitationsQuery,
  useResendInvitationMutation,
  useCancelInvitationMutation,
  useGetInvitationDetailsQuery,
  useGetInvitationMetricsQuery,
  useSubmitJoinRequestMutation,
  useGetJoinRequestsQuery,
  useGetMyJoinRequestsQuery,
  useApproveJoinRequestMutation,
  useDenyJoinRequestMutation,
  useCancelJoinRequestMutation,
  useGetJoinRequestDetailsQuery,
  useGetJoinRequestMetricsQuery,
  useSearchPublicStudiosQuery,
  useGetBrowsableStudiosQuery,
  useGetRoleHistoryQuery,
  useBulkRoleChangeMutation,
  useGetRoleManagementStatsQuery,
  useGetMemberActivitiesQuery,
  useGetActivityMetricsQuery,
  useGetEngagementDataQuery,
  useGetMemberActivitySummaryQuery,
  useGetUsageMetricsQuery,
  useGetQuotaOverviewQuery,
  useGetUsageHistoryQuery,
  useGetUsageChartDataQuery,
  useDismissUsageWarningMutation,
  useGetUsageWarningsQuery,
  useGetInvoicesQuery,
  useGetInvoiceDetailsQuery,
  useDownloadInvoiceMutation,
  useBulkDownloadInvoicesMutation,
  usePerformInvoiceActionMutation,
  usePerformBulkInvoiceActionMutation,
  useGetPaymentFailuresQuery,
  useRetryPaymentMutation,
  useDismissPaymentFailureMutation,
  useGetBillingMetricsQuery,
  useGetBillingSettingsQuery,
  useUpdateBillingSettingsMutation,
  useGetToolAccessQuery,
  useUpdateToolAccessMutation,
  useCheckToolAvailabilityQuery,
  useLazyCheckToolAvailabilityQuery,
  useGenerateToolAccessReportQuery,
  useBulkUpdateToolAccessMutation,
  useResetToolAccessDefaultsMutation,
  useGetBillingDashboardQuery,
  useGetBillingInvoicesQuery,
  useDownloadBillingInvoiceMutation,
  useBulkDownloadBillingInvoicesMutation,
} = studiosApi;
